import React, { useMemo } from "react";
import { useUser } from "src/shared/context/UserContext";
import { ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import { Order } from "src/shared/types";

interface Props {
  order: Order | null;
  remaining: number;
}

const toBoolean = (value: any): boolean => value === true || value === "true";

type RowConfig = {
  label: string;
  getValue: () => number | string;
  isBold?: boolean;
  isBordered?: boolean;
  getColorClass?: (val: number) => string;
  condition?: boolean;
};

export default function PaymentDetailsCard({ order, remaining }: Props) {
  const { userConfigration } = useUser();

  // ✅ Get the original order (ORDER_CREATED)
  const originalOrder = order?.orders?.find((o) => o.paymentSource === "ORDER_CREATED");
  const returnOrders = order?.orders?.filter((o) => o.paymentSource === "RETURN") || [];

  // ✅ Get ALL non-return payments (ORDER_CREATED + ORDER_PAGE + LEDGER_PAGE)
  const nonReturnPayments = order?.paymentHistory?.filter((p) => p.paymentSource !== "RETURN") || [];

  // ✅ Calculate total return amounts from ALL returns
  const totalReturnAdjustment = returnOrders.reduce((sum, o) => sum + Number((o as any).adjustAmount || 0), 0);
  const totalReturnDiscount = returnOrders.reduce((sum, o) => sum + Number(o.discount || 0), 0);
  const totalReturnDeliveryFee = returnOrders.reduce((sum, o) => sum + Number(o.deliveryFee || 0), 0);
  const totalReturnParcelFee = returnOrders.reduce((sum, o) => sum + Number(o.parcelFee || 0), 0);

  // ✅ Calculate total return product amount from orderDetails
  const returnOrderDetails = order?.orderDetails?.filter((d) => d.paymentSource === "RETURN") || [];
  let totalReturnProductAmount = 0;
  returnOrderDetails.forEach((detail) => {
    detail.products?.forEach((p) => {
      totalReturnProductAmount += Math.abs(Number(p.quantity || 0)) * Number(p.unitPrice || 0);
    });
  });

  // ✅ Calculate net values
  const originalOrderAmount = Number(originalOrder?.orderAmount || 0);
  const originalDiscount = Number(originalOrder?.discount || 0);
  const originalDeliveryFee = Number(originalOrder?.deliveryFee || 0);
  const originalParcelFee = Number(originalOrder?.parcelFee || 0);
  const originalFinalAmount = Number(originalOrder?.finalAmount || 0);

  const netOrderAmount = originalOrderAmount - totalReturnProductAmount;
  const netDiscount = Math.max(0, originalDiscount - totalReturnDiscount);
  const netDeliveryFee = Math.max(0, originalDeliveryFee - totalReturnDeliveryFee);
  const netParcelFee = Math.max(0, originalParcelFee - totalReturnParcelFee);
  const netFinalAmount = originalFinalAmount - totalReturnAdjustment;

  // ✅ Calculate total paid from ALL non-return payments
  const totalPaid = nonReturnPayments.reduce((sum, p) => sum + Number(p.receivedAmount || 0), 0);

  const features = useMemo(
    () => ({
      deliveryFee: toBoolean(userConfigration?.deliveryFeeEnabled),
      parcelFee: toBoolean(userConfigration?.parcelFeeEnabled),
    }),
    [userConfigration],
  );

  // ✅ Get unique payment methods
  const uniquePaymentMethods = Array.from(new Set(nonReturnPayments.map((p) => p.paymentMethod).filter(Boolean))).join(", ") || "-";

  if (!order) return null;

  // Row configuration - uses net values after returns
  const rows: RowConfig[] = [
    { label: "Sub Total", getValue: () => netOrderAmount },
    { label: "Discount", getValue: () => netDiscount },
    { label: "Delivery Fee", getValue: () => netDeliveryFee, condition: features.deliveryFee },
    { label: "Parcel Fee", getValue: () => netParcelFee, condition: features.parcelFee },
    { label: "Order Amount", getValue: () => netFinalAmount, isBold: true },
    {
      label: "Prev Return Amount (-)",
      getValue: () => totalReturnAdjustment,
      isBordered: true,
    },
    {
      label: "Final Amount",
      getValue: () => netFinalAmount - totalReturnAdjustment,
      isBold: true,
    },
    {
      label: "Total Paid",
      getValue: () => totalPaid,
      isBold: true,
      isBordered: true,
    },
    {
      label: "Remaining",
      getValue: () => remaining,
      isBold: true,
      isBordered: true,
      getColorClass: (val: number) => (val > 0 ? "text-red-500" : "text-green-600"),
    },
    { label: "Payment Type", getValue: () => uniquePaymentMethods },
  ];

  const visibleRows = rows.filter((row) => row.condition !== false);

  return (
    <div className="bg-white rounded shadow">
      <div className={`${ThemeBasedColorHeader()} text-white p-2 rounded-t`}>Payment Details</div>
      <div className="p-2 text-sm space-y-1">
        {visibleRows.map((row, index) => {
          const value = row.getValue();
          const isNumeric = typeof value === "number";
          const colorClass = isNumeric && row.getColorClass ? row.getColorClass(value) : "";

          return (
            <div key={index} className={`flex justify-between ${row.isBold ? "font-medium" : ""} ${row.isBordered ? "border-t border-dashed border-gray-400 pt-1" : ""}`}>
              <span>{row.label}</span>
              <span className={colorClass}>{isNumeric ? (value as number).toFixed(2) : value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
